// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type {
  FormulaContext,
  FunctionSignature,
} from "@/lib/formula-validation-service";
import { cn } from "@/lib/utils";
import type { ColumnReference } from "@/types/formula-column";
import { FormulaSyntaxHint } from "./formula-syntax-hint";

interface FormulaSuggestion {
  type: "column" | "function";
  value: string;
  label: string;
  description?: string;
  category?: string;
  icon?: React.ReactNode;
}

interface FormulaAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  columnReferences: ColumnReference[];
  availableFunctions: string[];
  placeholder?: string;
  className?: string;
  onInsertAtCursor?: (insertFunction: (text: string) => void) => void;
  validationService: {
    parseFormulaContext(
      formula: string,
      cursorPosition: number,
    ): FormulaContext;
    getFunctionSignature(functionName: string): FunctionSignature | undefined;
  };
}

// Function descriptions for better UX
export const FUNCTION_DESCRIPTIONS: Record<string, string> = {
  ABS: "Returns the absolute value of a number",
  AND: "Returns TRUE if all conditions are true",
  AVERAGE: "Calculates the mean of numbers",
  CONCAT: "Joins text strings together",
  COUNT: "Counts the number of cells that contain numbers",
  DATE: "Creates a date from year, month, and day values",
  DAY: "Extracts the day from a date",
  IF: "Returns one value if condition is true, another if false",
  INDEX: "Returns a value from a table by row and column position",
  LEN: "Returns the length of a text string",
  LOWER: "Converts text to lowercase",
  MATCH: "Finds the position of a value in a range",
  MAX: "Returns the largest value",
  MIN: "Returns the smallest value",
  MONTH: "Extracts the month from a date",
  NOT: "Returns the opposite of a logical value",
  OR: "Returns TRUE if any condition is true",
  POWER: "Returns the result of a number raised to a power",
  ROUND: "Rounds a number to specified decimal places",
  SQRT: "Returns the square root of a number",
  SUM: "Adds all numbers in a range",
  TODAY: "Returns the current date",
  UPPER: "Converts text to uppercase",
  VLOOKUP: "Looks up values in a table by matching the first column",
  YEAR: "Extracts the year from a date",
};

export function FormulaAutocomplete({
  value,
  onChange,
  onKeyDown,
  columnReferences,
  availableFunctions,
  placeholder = "Enter formula...",
  className,
  onInsertAtCursor,
  validationService,
}: FormulaAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<FormulaSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [formulaContext, setFormulaContext] = useState<FormulaContext>({
    type: "root",
    position: 0,
    text: "",
    isInsideFunction: false,
  });
  const [stableFunctionSignature, setStableFunctionSignature] = useState<
    FunctionSignature | undefined
  >();
  const [justAppliedSuggestion, setJustAppliedSuggestion] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const signatureTimeoutRef = useRef<NodeJS.Timeout>();

  // Get the current word being typed at cursor position
  const getCurrentWord = useCallback((text: string, position: number) => {
    const beforeCursor = text.substring(0, position);
    const afterCursor = text.substring(position);

    // Find word boundaries (letters, numbers, underscores, dots)
    const wordStart =
      Math.max(
        beforeCursor.lastIndexOf(" "),
        beforeCursor.lastIndexOf("("),
        beforeCursor.lastIndexOf(","),
        beforeCursor.lastIndexOf("="),
        beforeCursor.lastIndexOf("+"),
        beforeCursor.lastIndexOf("-"),
        beforeCursor.lastIndexOf("*"),
        beforeCursor.lastIndexOf("/"),
        beforeCursor.lastIndexOf(">"),
        beforeCursor.lastIndexOf("<"),
      ) + 1;

    const wordEnd = Math.min(
      afterCursor.search(/[\s(),=+\-*/<>]/),
      afterCursor.length,
    );

    const actualWordEnd = wordEnd === -1 ? afterCursor.length : wordEnd;
    const currentWord =
      beforeCursor.substring(wordStart) +
      afterCursor.substring(0, actualWordEnd);

    return {
      word: currentWord,
      start: wordStart,
      end: position + actualWordEnd,
    };
  }, []);

  // Insert text at current cursor position
  const insertAtCursor = useCallback(
    (textToInsert: string) => {
      if (!textareaRef.current || !textToInsert) {
        return;
      }

      const textarea = textareaRef.current;

      // Focus the textarea first
      textarea.focus();

      // Use the current cursor position from React state, fallback to DOM
      let start = cursorPosition;
      let end = cursorPosition;

      // If cursor position seems stale, get fresh position from DOM
      if (document.activeElement === textarea) {
        start = textarea.selectionStart || cursorPosition;
        end = textarea.selectionEnd || cursorPosition;
      }

      // Create new value with inserted text at cursor position
      const newValue = value.slice(0, start) + textToInsert + value.slice(end);

      // Update React state
      onChange(newValue);

      // Set cursor position after React has updated
      const newCursorPos = start + textToInsert.length;

      requestAnimationFrame(() => {
        if (textarea && document.activeElement === textarea) {
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          setCursorPosition(newCursorPos);
        }
      });
    },
    [onChange, value, cursorPosition],
  );

  // Expose insertAtCursor to parent component
  useEffect(() => {
    if (onInsertAtCursor && textareaRef.current) {
      onInsertAtCursor(insertAtCursor);
    }
  }, [insertAtCursor, onInsertAtCursor]);

  // Generate suggestions based on current input
  const generateSuggestions = useCallback(
    (currentWord: string): FormulaSuggestion[] => {
      if (!currentWord || currentWord.length < 1) return [];

      const upperWord = currentWord.toUpperCase();
      const suggestions: FormulaSuggestion[] = [];

      // Add column suggestions
      columnReferences.forEach((col) => {
        const upperKey = col.key.toUpperCase();
        const upperDisplayName = col.displayName.toUpperCase();

        // Include if it matches (including exact matches)
        if (
          upperKey.includes(upperWord) ||
          upperDisplayName.includes(upperWord)
        ) {
          suggestions.push({
            type: "column",
            value: upperKey,
            label: col.key.toUpperCase(),
            description: col.description,
            category: col.category,
          });
        }
      });

      // Add function suggestions
      availableFunctions.forEach((func) => {
        const upperFunc = func.toUpperCase();
        // Include if it matches (including exact matches)
        if (upperFunc.includes(upperWord)) {
          suggestions.push({
            type: "function",
            value: `${func}()`,
            label: func,
            description: FUNCTION_DESCRIPTIONS[func] || `${func} function`,
          });
        }
      });

      // Sort suggestions: by length first (shorter matches are more relevant), then alphabetically
      return suggestions
        .sort((a, b) => {
          if (a.label.length !== b.label.length)
            return a.label.length - b.label.length;
          return a.label.localeCompare(b.label);
        })
        .slice(0, 8); // Limit to 8 suggestions
    },
    [columnReferences, availableFunctions],
  );

  // Handle cursor position changes
  const handleSelectionChange = useCallback(() => {
    if (textareaRef.current) {
      const newPos = textareaRef.current.selectionStart || 0;
      setCursorPosition(newPos);
    }
  }, []);

  // Handle focus events
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleBlur = useCallback(() => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Small delay to allow suggestion clicks to register
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setShowSuggestions(false);
      blurTimeoutRef.current = null;
    }, 150);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Update suggestions and formula context when value or cursor position changes
  useEffect(() => {
    // Don't show suggestions immediately after applying one or when not focused
    if (justAppliedSuggestion || !isFocused) {
      setShowSuggestions(false);
      return;
    }

    // Parse formula context
    const context = validationService.parseFormulaContext(
      value,
      cursorPosition,
    );
    setFormulaContext(context);

    // Generate suggestions based on context
    const { word } = getCurrentWord(value, cursorPosition);
    const newSuggestions = generateSuggestions(word);

    // When inside a function, prioritize column suggestions for parameters
    if (context.isInsideFunction) {
      // Show suggestions but don't auto-hide them as quickly
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0 || word.length > 0);
    } else {
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }

    setSelectedIndex(0);

    // Handle function signature stability
    const currentSignature =
      context.isInsideFunction && context.functionName
        ? validationService.getFunctionSignature(context.functionName)
        : undefined;

    if (currentSignature) {
      // Clear any existing timeout
      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }
      // Immediately show new signature
      setStableFunctionSignature(currentSignature);
    } else if (stableFunctionSignature) {
      // Clear any existing timeout
      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }
      // Delay hiding the signature to prevent flashing
      signatureTimeoutRef.current = setTimeout(() => {
        setStableFunctionSignature(undefined);
      }, 150);
    }
  }, [
    value,
    cursorPosition,
    getCurrentWord,
    generateSuggestions,
    stableFunctionSignature,
    justAppliedSuggestion,
    isFocused,
    validationService,
  ]);

  // Handle suggestion selection
  const applySuggestion = useCallback(
    (suggestion: FormulaSuggestion) => {
      const { start, end } = getCurrentWord(value, cursorPosition);
      const beforeWord = value.substring(0, start);
      const afterWord = value.substring(end);

      let insertValue = suggestion.value;

      // For functions, position cursor inside parentheses
      if (suggestion.type === "function") {
        insertValue = suggestion.value.replace("()", "(");
      }

      const newValue = beforeWord + insertValue + afterWord;
      const newCursorPos = start + insertValue.length;

      setJustAppliedSuggestion(true);
      onChange(newValue);
      setShowSuggestions(false);

      // Set cursor position after update
      setTimeout(() => {
        if (textareaRef.current) {
          // Position cursor inside parentheses for functions
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
        // Reset the flag after a short delay
        setTimeout(() => setJustAppliedSuggestion(false), 100);
      }, 0);
    },
    [value, cursorPosition, getCurrentWord, onChange],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showSuggestions) {
        onKeyDown?.(e);
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            Math.min(prev + 1, suggestions.length - 1),
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
        case "Tab":
          if (suggestions[selectedIndex]) {
            e.preventDefault();
            applySuggestion(suggestions[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowSuggestions(false);
          break;
        default:
          onKeyDown?.(e);
      }
    },
    [showSuggestions, suggestions, selectedIndex, applySuggestion, onKeyDown],
  );

  // Handle input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Scroll selected item into view
  useEffect(() => {
    if (suggestionsRef.current && showSuggestions) {
      const selectedElement = suggestionsRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex, showSuggestions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      {/* Textarea with its own positioning context for autocomplete */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSelect={handleSelectionChange}
          onFocus={() => {
            handleFocus();
            handleSelectionChange();
          }}
          onBlur={handleBlur}
          onClick={handleSelectionChange}
          placeholder={placeholder}
          className={cn(
            "font-mono text-sm min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none",
            className,
          )}
        />

        {/* Auto-suggest popup - positioned immediately below textarea */}
        {showSuggestions && suggestions.length > 0 && (
          <Card
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto border shadow-lg"
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.type}-${suggestion.value}`}
                className={cn(
                  "px-3 py-2 cursor-pointer border-b last:border-b-0",
                  index === selectedIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50",
                )}
                onClick={() => applySuggestion(suggestion)}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-sm font-medium">
                      {suggestion.label}
                    </code>
                    <Badge
                      className={cn(
                        "text-xs",
                        suggestion.type === "column"
                          ? "bg-primary/10 text-primary"
                          : "bg-success/10 text-success",
                      )}
                    >
                      {suggestion.type === "column"
                        ? suggestion.category
                        : "function"}
                    </Badge>
                  </div>
                  {suggestion.description && (
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {suggestion.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* Function Syntax Hint - Always render container to prevent jumping */}
      <div className="mt-2 min-h-[24px]">
        {stableFunctionSignature && (
          <FormulaSyntaxHint
            signature={stableFunctionSignature}
            currentParameterIndex={formulaContext.currentParameterIndex}
          />
        )}
      </div>
    </div>
  );
}
